"use client"

import { DefaultImage } from "@/lib/external-components"
import { generateImagePath } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { Autoplay, Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface SliderItem extends CommonStylesProps {
  image1: string | { url: string; alternativeText?: string }
  image2?: string | { url: string; alternativeText?: string }
  tag?: string
  textField: {
    country: string
    countryTextSize: string
    countryTextWeight: string
    countryLineHeight: string
    province: string
    provinceTextSize: string
    provinceTextWeight: string
    provinceLineHeight: string
  }
  textContainerStyles?: {
    position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center"
    fontSize?: string
    fontWeight?: "light" | "normal" | "bold" | "extrabold"
    color?: string
    backgroundColor?: string
    padding?: string
    gap?: string
  }
  mediaStyles?: {
    width: string
    margin: string
  }
}

export interface SliderProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  sliderItems?: SliderItem[]
  controlButton: boolean
  heightSliderDesktop?: string
  heightSliderMobile?: string
  gapSlider?: string
}

export const RenderConfig: ComponentConfig<SliderProps> = {
  render: ({
    className,
    type,
    controlButton,
    styles,
    gapSlider,
    responsiveType,
    heightSliderDesktop,
    heightSliderMobile,
    sliderItems = [],
  }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const gapValue = gapSlider ? parseInt(gapSlider.replace("px", "")) || 24 : 24
    const [currentIndex, setCurrentIndex] = useState(0)
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [shouldCenter, setShouldCenter] = useState(false)
    const swiperRef = useRef<SwiperType | null>(null)

    const checkUnderflow = useCallback(
      (el: HTMLElement) => {
        const containerWidth = el.offsetWidth
        const slides = el.querySelectorAll<HTMLElement>(".swiper-slide:not(.swiper-slide-duplicate)")
        if (!slides.length) return
        let totalWidth = gapValue * (slides.length - 1)
        slides.forEach(slide => {
          totalWidth += slide.offsetWidth
        })
        setShouldCenter(totalWidth < containerWidth)
      },
      [gapValue],
    )

    useEffect(() => {
      const handleResize = () => {
        if (swiperRef.current?.el) checkUnderflow(swiperRef.current.el)
      }
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }, [checkUnderflow])
    const positionClasses = {
      "top-left": "absolute top-0 left-0",
      "top-right": "absolute top-0 right-0",
      "bottom-left": "absolute bottom-0 left-0",
      "bottom-right": "absolute bottom-0 right-0",
      center: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
    }

    const mergedCSS = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }
    const responsiveCSS = generateResponsiveCSS(id, mergedCSS || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        {shouldCenter && <style>{`#${id} .swiper-wrapper { justify-content: center !important; }`}</style>}
        <div id={id} className="relative w-full overflow-hidden">
          <Swiper
            key={String(shouldCenter)}
            onSwiper={swiper => {
              swiperRef.current = swiper
              requestAnimationFrame(() => {
                if (swiper.el) checkUnderflow(swiper.el)
              })
            }}
            onSlideChange={swiper => {
              setCurrentIndex(swiper.activeIndex)
            }}
            modules={[Navigation, Autoplay]}
            spaceBetween={gapValue}
            slidesPerView="auto"
            navigation={
              controlButton
                ? {
                    nextEl: `.swiper-button-next-${id}`,
                    prevEl: `.swiper-button-prev-${id}`,
                  }
                : false
            }
            grabCursor={true}
            className="!overflow-visible"
            breakpoints={{
              0: {
                spaceBetween: gapValue,
              },
              1024: {
                spaceBetween: gapValue,
              },
            }}
            loop={!shouldCenter}
            autoplay={shouldCenter ? false : { delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
          >
            {sliderItems.map((item, index) => (
              <SwiperSlide
                key={index}
                className="!w-auto"
                style={{
                  width:
                    typeof window !== "undefined" && window.innerWidth >= 1024
                      ? item.mediaStyles?.width
                      : typeof window !== "undefined" &&
                          window.innerWidth <= 1024 &&
                          parseInt(String(item.mediaStyles?.width), 10) > 500
                        ? "375px"
                        : item.mediaStyles?.width,
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className="relative"
                  style={{
                    height:
                      typeof window !== "undefined" && window.innerWidth >= 1024
                        ? heightSliderDesktop
                        : heightSliderMobile || "auto",
                  }}
                >
                  {item.textField && item.textContainerStyles && (
                    <div
                      className={`absolute ${
                        positionClasses[item.textContainerStyles.position || "center"]
                      } flex flex-col `}
                      style={{
                        color: item.textContainerStyles.color || "white",
                        gap: item.textContainerStyles.gap || "8px",
                        padding: item.textContainerStyles.padding || "24px",
                        zIndex: 20,
                      }}
                    >
                      {item.textField.country && (
                        <div className="text-base leading-[19px] font-normal text-white">{item.textField.country}</div>
                      )}
                      {item.textField.province && (
                        <div className="text-xl leading-[30px] font-semibold text-white">{item.textField.province}</div>
                      )}
                    </div>
                  )}
                  <div
                    className={`relative lg:h-full lg:rounded-2xl ${
                      parseInt(String(item.mediaStyles?.width), 10) < 500 && "rounded-2xl"
                    } overflow-hidden`}
                    style={{
                      width:
                        typeof window !== "undefined" && window.innerWidth >= 1024
                          ? item.mediaStyles?.width
                          : typeof window !== "undefined" &&
                              window.innerWidth <= 1024 &&
                              parseInt(String(item.mediaStyles?.width), 10) > 500
                            ? "375px"
                            : item.mediaStyles?.width,
                      height: typeof window !== "undefined" && window.innerWidth >= 1024 ? "100%" : heightSliderMobile,
                    }}
                  >
                    <Image
                      src={
                        typeof item.image1 === "object"
                          ? generateImagePath((item.image1 as { url: string }).url, "medium")
                          : item.image1
                            ? generateImagePath(item.image1, "medium")
                            : DefaultImage
                      }
                      alt={
                        typeof item.image1 === "object"
                          ? ((item.image1 as { alternativeText?: string }).alternativeText ?? `Slide ${index + 1}`)
                          : item.image1 || `Slide ${index + 1}`
                      }
                      fill
                      className="object-cover transition-all duration-300 ease-out"
                      sizes="(max-width: 768px) 375px, (max-width: 1024px) 500px, 100vw"
                      priority={index === 0}
                    />
                  </div>

                  {item.textField?.country && item.textField?.province && (
                    <div
                      className="absolute bottom-0 left-0 w-full rounded-b-2xl"
                      style={{
                        height: "107px",
                        background: "linear-gradient(to top, #000000CC, #242F3E00)",
                        zIndex: 10,
                      }}
                    ></div>
                  )}
                  {item.image2 && (
                    <div className="absolute top-0 left-0 w-full h-full rounded-2xl overflow-hidden">
                      <Image
                        src={
                          typeof item.image2 === "object"
                            ? generateImagePath((item.image2 as { url: string }).url, "medium")
                            : item.image2
                              ? generateImagePath(item.image2, "medium")
                              : DefaultImage
                        }
                        alt={
                          typeof item.image2 === "object"
                            ? ((item.image2 as { alternativeText?: string }).alternativeText ??
                              `Slide ${index + 1} hover`)
                            : item.image2 || `Slide ${index + 1} hover`
                        }
                        fill
                        className={`object-cover transition-all duration-300 ease-out ${
                          hoveredIndex === index ? "opacity-100" : "opacity-0"
                        }`}
                        sizes="(max-width: 768px) 375px, (max-width: 1024px) 500px, 100vw"
                      />
                    </div>
                  )}

                  {hoveredIndex === index && item.image2 && (
                    <button
                      className="absolute bottom-[33.5px] right-6 bg-[#FFFFFF33] text-white p-3 rounded-full z-30 backdrop-blur-sm"
                      style={{
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                        transition: "transform 300ms ease-in-out",
                      }}
                      aria-label="View more information"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M5.77734 2.94434L11.3329 8.49989L5.77734 14.0554"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {controlButton && (
            <div className="justify-center gap-2 lg:mt-16 lg:flex hidden">
              <button
                className={`swiper-button-prev-${id} flex p-1 rounded-full w-10 h-10 items-center justify-center`}
                style={{ backgroundColor: "#EBEDF0" }}
                aria-label="Previous slide"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-chevron-left"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                className={`swiper-button-next-${id} flex p-1 rounded-full w-10 h-10 items-center justify-center`}
                style={{ backgroundColor: "#EBEDF0" }}
                aria-label="Next slide"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-chevron-right"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
          {sliderItems.length > 1 && parseInt(String(heightSliderMobile), 10) > 200 && (
            <div className="flex items-center w-full justify-center lg:hidden">
              <div className="flex justify-center mt-6 bg-[#242F3E0F] py-[10px] px-2 rounded-full">
                {sliderItems.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-2 mx-[5px] rounded-full transition-all ${
                      currentIndex === index ? "bg-[#000] w-6" : "bg-[#D9D9D9]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    )
  },
}
